import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { db } from '../../services/firebase';
import { collection, query, where, orderBy, onSnapshot, addDoc, serverTimestamp, getDocs, updateDoc, doc, deleteDoc, limit } from 'firebase/firestore';
import { MdSend, MdChat, MdPerson, MdArrowBack, MdEdit, MdDelete } from 'react-icons/md';

const formatTime = (timestamp) => {
  if (!timestamp) return '';
  let date;
  if (typeof timestamp.toDate === 'function') {
    date = timestamp.toDate();
  } else if (timestamp.seconds !== undefined) {
    date = new Date(timestamp.seconds * 1000);
  } else {
    date = new Date(timestamp);
  }
  
  if (isNaN(date.getTime())) return '';
  
  const hours = date.getHours().toString().padStart(2, '0');
  const minutes = date.getMinutes().toString().padStart(2, '0');
  return `${hours}:${minutes}`;
};

const TeacherMessages = () => {
  const { userProfile } = useAuth();
  
  const [chats, setChats] = useState([]);
  const [selectedChat, setSelectedChat] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loadingChats, setLoadingChats] = useState(true);
  const [editingMessage, setEditingMessage] = useState(null);
  
  const messagesEndRef = useRef(null);

  // Fetch chat list where teacher is a participant (or just all chats for MVP)
  useEffect(() => {
    if (!userProfile) return;
    
    // MVP: In a real app, you would filter by teacherId or just fetch chats where the teacher is involved.
    // For MVP, we'll fetch all chats to make it easy to test.
    const chatsRef = collection(db, 'chats');
    // const q = query(chatsRef, where('teacherId', '==', userProfile.uid), orderBy('lastMessageTime', 'desc'));
    const q = query(chatsRef, orderBy('lastMessageTime', 'desc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const chatList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setChats(chatList);
      setLoadingChats(false);
    }, (error) => {
      console.error("Error fetching chats:", error);
      setLoadingChats(false);
    });

    return () => unsubscribe();
  }, [userProfile]);

  // Fetch messages for the selected chat
  useEffect(() => {
    setEditingMessage(null);
    setNewMessage('');
    if (!selectedChat) {
      setMessages([]);
      return;
    }

    const messagesRef = collection(db, 'chats', selectedChat.id, 'messages');
    const q = query(messagesRef, orderBy('timestamp', 'asc'));
    
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const msgList = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      setMessages(msgList);
      setTimeout(() => scrollToBottom(), 100);
    });

    return () => unsubscribe();
  }, [selectedChat]);

  // Auto-clear unread status if a chat is active or a new message arrives on it
  useEffect(() => {
    if (!selectedChat) return;
    const currentChat = chats.find(c => c.id === selectedChat.id);
    if (currentChat && currentChat.unreadByTeacher) {
      const clearUnread = async () => {
        try {
          await updateDoc(doc(db, 'chats', selectedChat.id), {
            unreadByTeacher: false
          });
        } catch (err) {
          console.error("Error clearing unread status:", err);
        }
      };
      clearUnread();
    }
  }, [selectedChat, chats]);

  const selectChat = async (chat) => {
    setSelectedChat(chat);
    if (chat.unreadByTeacher) {
      try {
        await updateDoc(doc(db, 'chats', chat.id), {
          unreadByTeacher: false
        });
      } catch (err) {
        console.error("Error clearing unread status on select:", err);
      }
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedChat) return;

    const messageText = newMessage.trim();
    setNewMessage(''); // optimistic clear

    try {
      if (editingMessage) {
        // Update existing message
        await updateDoc(doc(db, 'chats', selectedChat.id, 'messages', editingMessage.id), {
          text: messageText,
          isEdited: true,
          updatedAt: serverTimestamp(),
        });

        // Update parent chat document if it was the last message
        const chatDocRef = doc(db, 'chats', selectedChat.id);
        const messagesRef = collection(db, 'chats', selectedChat.id, 'messages');
        const q = query(messagesRef, orderBy('timestamp', 'desc'), limit(1));
        const chatSnap = await getDocs(q);
        if (!chatSnap.empty && chatSnap.docs[0].id === editingMessage.id) {
          await updateDoc(chatDocRef, {
            lastMessage: messageText,
          });
        }

        setEditingMessage(null);
      } else {
        // 1. Add message to subcollection
        await addDoc(collection(db, 'chats', selectedChat.id, 'messages'), {
          senderId: userProfile.uid,
          senderName: userProfile.fullName || 'Teacher',
          text: messageText,
          timestamp: serverTimestamp(),
        });

        // 2. Update parent chat document
        await updateDoc(doc(db, 'chats', selectedChat.id), {
          lastMessage: messageText,
          lastMessageTime: serverTimestamp(),
          unreadByTeacher: false,
          unreadByStudent: true
        });

        // 3. Send a notification to the student
        if (selectedChat.studentId) {
          await addDoc(collection(db, 'notifications'), {
            title: 'New Message from Teacher',
            message: `${userProfile.fullName || 'A teacher'} replied to you: "${messageText.substring(0, 30)}${messageText.length > 30 ? '...' : ''}"`,
            type: 'info',
            action: 'teacher_reply',
            audience: selectedChat.studentId,
            teacherId: userProfile.uid,
            teacherName: userProfile.fullName || 'Teacher',
            timestamp: serverTimestamp(),
          });
        }
      }
    } catch (err) {
      console.error("Error sending/updating message:", err);
    }
  };

  const handleStartEdit = (msg) => {
    setEditingMessage(msg);
    setNewMessage(msg.text);
  };

  const handleDeleteMessage = async (msg) => {
    if (!window.confirm('Are you sure you want to delete this message?')) return;
    
    try {
      await deleteDoc(doc(db, 'chats', selectedChat.id, 'messages', msg.id));
      
      // If it was the last message, update parent chat's lastMessage
      const chatDocRef = doc(db, 'chats', selectedChat.id);
      const messagesRef = collection(db, 'chats', selectedChat.id, 'messages');
      const q = query(messagesRef, orderBy('timestamp', 'desc'), limit(1));
      const snap = await getDocs(q);
      if (!snap.empty) {
        const latestMsg = snap.docs[0].data();
        await updateDoc(chatDocRef, {
          lastMessage: latestMsg.text,
          lastMessageTime: latestMsg.timestamp || serverTimestamp()
        });
      } else {
        await updateDoc(chatDocRef, {
          lastMessage: 'No messages yet',
          lastMessageTime: serverTimestamp()
        });
      }
      alert('Message deleted successfully!');
    } catch (err) {
      console.error("Error deleting message:", err);
      alert('Failed to delete message. Please try again.');
    }
  };

  return (
    <div className="h-[calc(100vh-6rem)] w-full mx-auto flex flex-col md:flex-row gap-4 md:gap-6 p-2 md:p-4">
      {/* Sidebar - Chat List */}
      <div className={`${selectedChat ? 'hidden md:flex' : 'flex'} w-full md:w-1/3 bg-white rounded-2xl border border-gray-200 shadow-sm flex-col overflow-hidden`}>
        <div className="p-4 border-b border-gray-100 bg-gray-50">
          <h2 className="font-bold text-lg text-darkNavy">Student Messages</h2>
        </div>
        
        <div className="flex-1 overflow-y-auto">
          {loadingChats ? (
            <div className="flex justify-center p-8">
              <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : chats.length === 0 ? (
            <div className="p-8 text-center text-gray-500 text-sm">
              No active conversations yet.
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {chats.map(chat => (
                <button
                  key={chat.id}
                  onClick={() => selectChat(chat)}
                  className={`w-full text-left p-4 hover:bg-blue-50 transition-colors flex items-center gap-3 ${
                    selectedChat?.id === chat.id ? 'bg-blue-50 border-l-4 border-primary' : 'border-l-4 border-transparent'
                  }`}
                >
                  <div className="relative w-10 h-10 rounded-full bg-blue-100 text-primary flex items-center justify-center flex-shrink-0">
                    <MdPerson size={20} />
                    {chat.unreadByTeacher && (
                      <span className="absolute top-0 right-0 w-3 h-3 bg-red-500 rounded-full border-2 border-white animate-pulse" />
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <h4 className="font-bold text-darkNavy text-sm truncate flex items-center justify-between gap-2">
                      <span className="truncate">
                        {chat.studentName || 'Student'} 
                        {chat.subjectName && <span className="text-xs font-normal text-gray-500 ml-1">({chat.subjectName})</span>}
                      </span>
                      {chat.unreadByTeacher && (
                        <span className="bg-red-500 text-white text-[10px] font-bold px-2 py-0.5 rounded-full flex-shrink-0">
                          New
                        </span>
                      )}
                    </h4>
                    <p className={`text-xs truncate mt-0.5 ${chat.unreadByTeacher ? 'text-darkNavy font-semibold' : 'text-gray-500'}`}>{chat.lastMessage || 'No messages yet'}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Main Chat Area */}
      <div className={`${!selectedChat ? 'hidden md:flex' : 'flex'} flex-1 w-full bg-white rounded-2xl border border-gray-200 shadow-sm flex-col overflow-hidden`}>
        {selectedChat ? (
          <>
            {/* Chat Header */}
            <div className="p-4 border-b border-gray-100 bg-gray-50 flex items-center gap-3">
              <button 
                onClick={() => setSelectedChat(null)}
                className="md:hidden p-2 -ml-2 rounded-full hover:bg-gray-200 text-gray-600 transition-colors"
              >
                <MdArrowBack size={20} />
              </button>
              <div className="w-10 h-10 rounded-full bg-blue-100 text-primary flex items-center justify-center">
                <MdPerson size={20} />
              </div>
              <div>
                <h3 className="font-bold text-darkNavy">{selectedChat.studentName || 'Student'}</h3>
                <p className="text-xs text-gray-500">{selectedChat.subjectName || 'General Query'}</p>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 bg-gray-50/50">
              {messages.map((msg, index) => {
                const isMe = msg.senderId === userProfile.uid;
                return (
                  <div key={msg.id || index} className={`flex mb-4 group items-center gap-2 ${isMe ? 'justify-end' : 'justify-start'}`}>
                    {isMe && (
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity self-center">
                        <button 
                          type="button"
                          onClick={() => handleStartEdit(msg)} 
                          className="p-1 bg-white hover:bg-primary text-gray-500 hover:text-white rounded-lg transition-all shadow-sm border border-gray-100"
                          title="Edit message"
                        >
                          <MdEdit size={14} />
                        </button>
                        <button 
                          type="button"
                          onClick={() => handleDeleteMessage(msg)} 
                          className="p-1 bg-white hover:bg-red-500 text-gray-500 hover:text-white rounded-lg transition-all shadow-sm border border-gray-100"
                          title="Delete message"
                        >
                          <MdDelete size={14} />
                        </button>
                      </div>
                    )}

                    <div className={`max-w-[70%] rounded-2xl px-4 py-2.5 ${
                      isMe 
                        ? 'bg-primary text-white rounded-tr-sm' 
                        : 'bg-white border border-gray-200 text-darkNavy rounded-tl-sm'
                    }`}>
                      {!isMe && <p className="text-xs font-bold mb-1 text-primary">{msg.senderName || 'Student'}</p>}
                      <p className="text-sm leading-relaxed">
                        {msg.text}
                        {msg.isEdited && <span className="text-[10px] opacity-75 font-normal ml-1.5">(edited)</span>}
                      </p>
                      {msg.timestamp && (
                        <p className={`text-[10px] mt-1 text-right ${isMe ? 'text-blue-200' : 'text-gray-400'}`}>
                          {formatTime(msg.timestamp)}
                        </p>
                      )}
                    </div>

                    {!isMe && (
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity self-center">
                        <button 
                          type="button"
                          onClick={() => handleDeleteMessage(msg)} 
                          className="p-1 bg-white hover:bg-red-500 text-gray-500 hover:text-white rounded-lg transition-all shadow-sm border border-gray-100"
                          title="Delete message"
                        >
                          <MdDelete size={14} />
                        </button>
                      </div>
                    )}
                  </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="p-4 border-t border-gray-100 bg-white">
              {editingMessage && (
                <div className="px-4 py-2 bg-blue-50 border border-blue-100 rounded-xl text-xs text-blue-700 flex items-center justify-between mb-3">
                  <span className="truncate">Editing message: <span className="font-semibold">"{editingMessage.text}"</span></span>
                  <button 
                    type="button" 
                    onClick={() => { setEditingMessage(null); setNewMessage(''); }} 
                    className="text-blue-500 hover:text-blue-700 font-bold ml-2 flex-shrink-0"
                  >
                    Cancel
                  </button>
                </div>
              )}
              <form onSubmit={handleSendMessage} className="flex gap-2">
                <input
                  type="text"
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type your reply..."
                  className="flex-1 px-4 py-2.5 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-primary/50 transition-all"
                />
                <button
                  type="submit"
                  disabled={!newMessage.trim()}
                  className="px-4 py-2.5 bg-primary text-white rounded-xl hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  <MdSend size={20} />
                </button>
              </form>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400">
            <MdChat size={64} className="mb-4 text-gray-200" />
            <p>Select a student's message to start chatting</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default TeacherMessages;
